import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { AccountInfoViewModel, ActivityReservationInternalClient, ActivityReservationInternalModel, EnterpriseAccountClient, ReservationMethod } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import * as Icons from "Components/Icons";
import { Button } from "Components/ReactstrapSlim";
import { AvatarCircleWithCache } from "Components/AvatarCircle";
import { VenueCalendarModal } from "Components/VenueCalendarModal";
import { DateOnly } from "typings/DateTime";
import { toDateOnly } from "Utils/date";
import { userSelector } from "Utils/selectors";
import { UserPictureCache } from "Hooks";

import DetailsDropdown from "./DetailsDropdown";
import classes from "./SocialBanner.module.scss";
import classNames from "classnames";
import useMediaBreakpointUp from "Hooks/useMediaBreakpointUp";
import { NOT_DELETED_FILTER } from "Utils/ActivityReservationStatusFilterPresets";

interface SocialBannerProps {
   accountId: Guid,
   venueId: Guid
}

const toggleId = "sb-toggle";

const SocialBanner: React.FC<SocialBannerProps> = ({ accountId, venueId }) => {
   const enterpriseAccountClient = useApiClient(EnterpriseAccountClient);
   const activityInternalClient = useApiClient(ActivityReservationInternalClient);
   const userPictureCache = useRef<UserPictureCache>({});
   const user = useSelector(userSelector);
   const isBreakpointSupported = useMediaBreakpointUp("lg");
   const [reservations, setReservations] = useState<ActivityReservationInternalModel[]>([]);
   const [account, setAccount] = useState<AccountInfoViewModel>();
   const [prebinDate, setPrebinDate] = useState<DateOnly>();
   const [containerRect, setContainerRect] = useState<{ width: number, height: number }>({ width: 0, height: 0 });
   const [isDropdownOpen, setIsDropdownOpen] = useState(false);

   //Distinct reservations by email and map them to member info
   const collegues = [...new Map(reservations.map(r => {
      const [firstName, lastName] = r.memberFullName?.split(" ") ?? [];
      return [r.memberEmail, {
         id: r.memberId,
         email: r.memberEmail,
         firstName: firstName,
         lastName: lastName,
      }]
   })).values()];


   const loadEnterpriseAccount = async (abortSignal?: AbortSignal) => {
      try {
         setAccount(await enterpriseAccountClient.accountInfo(accountId));
      }
      catch {
         setAccount(undefined);
      }
   }

   const loadReservations = async (abortSignal?: AbortSignal) => {
      try {
         const startDate = new Date();
         const endDate = new Date();
         endDate.setDate(endDate.getDate() + 30);
         const data = await activityInternalClient.reservations(
            accountId,
            NOT_DELETED_FILTER,
            undefined,
            undefined,
            toDateOnly(startDate),
            toDateOnly(endDate),
            undefined,
            abortSignal
         );
         setReservations(data.filter(r =>
            r.venueId == venueId
            && r.memberId != user.id
            && r.reservationType != ReservationMethod.Monthly
         ));
      }
      catch {
         setReservations([]);
      }
   }

   const openPrebin = (date?: DateOnly) => {
      setPrebinDate(date ?? toDateOnly(new Date()));
   }

   const closePrebin = () => {
      setPrebinDate(undefined);
   }

   const toggleDropdown = (e: React.SyntheticEvent) => {      
      if(e.target instanceof HTMLElement)
      {         
         if(e.target.id == toggleId
            || e.target.closest(`#${toggleId}`) != null)
            return;
         
      }
      innerToggleDropdown();
   }
   const innerToggleDropdown = () => {
      setIsDropdownOpen((wasOpen) => !wasOpen);
   }

   const handleContainerRect = useCallback((node: HTMLDivElement) => {
      if (node) {
         setContainerRect({
            width: node.clientWidth,
            height: node.clientHeight,
         });
      }
   }, []);


   useEffect(() => {
      const abortController = new AbortController();

      Promise.all([
         loadReservations(abortController.signal),
         loadEnterpriseAccount(abortController.signal)
      ])

      return function cancel() {
         abortController.abort();
      }
   }, []);

   if (!account || !collegues.length || !isBreakpointSupported)
      return null;

   return (<div className={classes.wrapper}>
      <DetailsDropdown
         isOpen={isDropdownOpen}
         toggle={toggleDropdown}
         accountId={accountId}
         venueId={venueId}
         userPictureCache={userPictureCache.current}
         width={containerRect.width}
         height={containerRect.height}
         onOpenPrebin={openPrebin}
      />
      <div className={classes.container} ref={handleContainerRect}>
         <div className={classes.info}>
            <div className={classes.avatarsWrapper}>
               {collegues.slice(0, collegues.length > 3 ? 2 : 3).map((col, index) => <AvatarCircleWithCache
                  key={col.email}
                  cache={userPictureCache.current}
                  userId={col.id}
                  noborder={index == 0}
                  size="md"
                  email={col.email}
                  firstName={col.firstName}
                  lastName={col.lastName}
                  className={classes.avatar}
               />)}
               {collegues.length > 3 && <div className={classNames(classes.avatarPlus, classes.avatar, "avatar-circle avatar-circle-md")}>+{collegues.length - 2}</div>}
            </div>
            <div className={classes.text}>
               <div className={classes.title}>
                  You have {collegues.length} {collegues.length > 1 ? "colleagues who work" : "colleague who works"} in this location
               </div>
               <div className={classes.subtitle}>
                  See when your {account.name} colleagues are working here and join them.
               </div>
            </div>
         </div>
         <div className={classes.buttons}>
            <Button id={toggleId} color="link" className={classes.detailsToggle} onClick={innerToggleDropdown}>
               View details <Icons.Caret className={classNames(classes.caret, { [classes.caretOpen]: isDropdownOpen })} />
            </Button>
            <Button color="primary" className={classes.cta} onClick={() => openPrebin()}>
               Work here
            </Button>
         </div>
         {prebinDate && <VenueCalendarModal isOpen={true} venueId={venueId} selectedDate={prebinDate} toggle={closePrebin} />}
      </div>
   </div>)
}

export default SocialBanner;